/**@TODO */
/**
 * - Handling shallow queries for tab management
 */
import { useScreenSize } from '@front/components/hooks/useScreenSize'
import { useGetPublicCareerSiteQuery } from '@front/api/service-career-neo/useGetPublicCareerSiteQuery'
import {
  Fragment,
  Suspense,
  useState,
  type ChangeEvent,
  type ReactElement,
} from 'react'
import { Box, Chip, Stack, useTheme } from '@mui/material'
import {
  ExpandMoreOutlined,
  InfoOutlined,
  MailOutlined,
} from '@mui/icons-material'
import {
  Heading2,
  Heading4,
  SmallTextBold,
  TextBody,
  TextBodyBold,
} from '@front/components/data-display/typography/Typography'
import { Badge, BadgeStatus } from '@front/components/data-display/badge/Badge'
import { PlaceOutlined } from '@mui/icons-material'
import { Tabs } from '@front/components/navigation/tabs/Tabs'
import { useTranslation } from 'react-i18next'
import { benefitsList } from '../../constants/benefitsList'
import { IconLink } from '../shared/icon-link-styled/IconLinkStyled'
import { VanillaButtonStyle } from '@front/shared/constants/styles/VanillaButtonStyle'
import { JobAdsFiltersStorageProvider } from '../hooks/useJobAdsFiltersStorage'
import { careerSocialNetworks } from '@front/shared/constants/styles/career-social-networks'
import { getRuntimeConfig } from '@front/env/config/career'
import {
  CareerThemeColors,
  type CareerColorKey,
} from '@front/shared/constants/styles/career-theme-colors'
import CareerWidget from '../shared/career-widget/CareerWidget'
import type { Widget } from '@front/type/career/career-pages.type'
import JobAdsTab from '../sections/JobAdsTab'
import { JobCardSkeleton } from '../shared/job-card/JobCardSkeleton'
import { useGetJobAds } from '../hooks/useGetJobAds'
import EmptyPage from '../sections/empty-page/EmptyPage'
import PresentationTab from '../sections/PresentationTab'
import MoreCareerInfoDialog from '../dialogs/MoreCareerInfoDialog'
import { accessModesList } from '../../constants/accessModesList'
import { AppButton } from '@front/components/inputs/app-button'
import PageTabSelectDialog from '../dialogs/PageTabSelectDialog'
import { InstitutionHeaderLogo } from '../shared/institution-header-logo/InstitutionHeaderLogo'

type PageProps = {
  slug: string
}

function CareerChip({ icon, label }: { icon: ReactElement; label: string }) {
  const theme = useTheme()

  return (
    <Chip
      icon={icon}
      label={label}
      variant="outlined"
      size="small"
      sx={{
        backgroundColor: theme.palette.background.surface,
        paddingLeft: 0.5,
        paddingRight: 0.5,
        '& .MuiChip-icon': {
          width: 16,
          height: 16,
          color: theme.palette.text.primary,
        },
      }}
    />
  )
}

export default function InstitutionPage({ slug }: PageProps) {
  // System
  const theme = useTheme()
  const { t: tCareer } = useTranslation('network.career_website')
  const { isSmallScreen } = useScreenSize()
  const {
    public: { googleApiKey },
  } = getRuntimeConfig()
  const [moreInfo, setMoreInfo] = useState<boolean>(false)
  const [tabSelector, setTabSelector] = useState<boolean>(false)

  // Tabs
  const [currentTab, setCurrentTab] = useState<number>(0)

  const handleChangeTab = (ev?: ChangeEvent<unknown>, index?: number) => {
    ev?.preventDefault()
    setCurrentTab(index as number)
  }

  const handleChangePage = (index: number) => {
    setCurrentTab(index)
  }

  const {
    data: institutionData,
    isLoading: institutionIsLoading,
    isError: institutionIsError,
  } = useGetPublicCareerSiteQuery(slug as string)

  const { total: totalOffers } = useGetJobAds(
    institutionData?.institutionUuid as string,
    null,
  )

  const themeKey: CareerColorKey =
    institutionData?.theme && institutionData.theme in CareerThemeColors
      ? (institutionData.theme as CareerColorKey)
      : 'red-1'

  const benefits = benefitsList
    .map((el) => el.items)
    .flat()
    .filter(({ itemId }) => institutionData?.benefits?.includes(itemId))

  // Google Maps
  /**@TODO */
  const mapsAddress = institutionData?.address?.replaceAll(' ', '+') ?? ''
  const mapsAddressUrl = `https://www.google.fr/maps/place/${mapsAddress}`
  const mapsStaticUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${mapsAddress}&zoom=15&size=368x220&maptype=roadmap&markers=color:red%7C${mapsAddress}&key=${googleApiKey}`

  const goToMaps = () => {
    window.open(mapsAddressUrl, '_blank', 'noopener, noreferrer')
  }

  const accessModes = accessModesList.filter(({ itemId }) =>
    institutionData?.accessOptions?.includes(itemId),
  )

  const socials = Object.entries({
    facebookLink: institutionData?.facebookLink,
    instagramLink: institutionData?.instagramLink,
    linkedinLink: institutionData?.linkedinLink,
    youtubeLink: institutionData?.youtubeLink,
  })
    .map(([key, url]) => ({
      url,
      social: careerSocialNetworks[key as keyof typeof careerSocialNetworks],
    }))
    .filter((entry) => Boolean(entry.url))

  return (
    <>
      {institutionIsError ? (
        <EmptyPage mode="institution" />
      ) : (
        <Stack
          sx={{
            position: 'relative',
            minHeight: '100vh',
            flex: 1,
            backgroundColor: theme.palette.background.subtle,
            alignItems: 'center',
          }}
        >
          {/**@ImageHeader */}
          <Stack
            sx={{
              width: '100%',
              height: { xs: '120px', md: '264px' },
              backgroundColor: CareerThemeColors[themeKey].imageBackgroundColor,
              opacity: 0.83,
              position: 'absolute',
              top: 0,
            }}
          >
            {/**@InstitutionImageHere */}
            {institutionData?.coverPictureUrl && (
              <img
                src={institutionData.coverPictureUrl}
                alt="header-background"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            <Box
              sx={{
                width: '100%',
                height: { xs: '120px', md: '264px' },
                position: 'absolute',
                backgroundColor:
                  CareerThemeColors[themeKey].imageBackgroundColor,
                opacity: 0.83,
              }}
            />
          </Stack>

          {/**@PageContentContainer */}
          <Stack
            sx={{
              paddingTop: { xs: '64px', md: '162px' },
              paddingLeft: { xs: 2, md: 17 },
              paddingRight: { xs: 2, md: 17 },
              width: '100%',
              maxWidth: '1440px',
              position: 'relative',
            }}
          >
            {/**@PageHeader */}
            <Stack
              sx={{
                flexDirection: { xs: 'column', md: 'row' },
                marginBottom: 2,
              }}
            >
              {/**@InstitutionImage */}
              <Stack
                sx={{
                  width: { xs: '72px', md: '150px' },
                  height: { xs: '72px', md: '150px' },
                  borderRadius: '4px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.background.surface,
                  border: 'solid 1px',
                  borderColor: theme.palette.border['brand-subtle'],
                  padding: { xs: 0, md: 2 },
                }}
              >
                <Stack
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#fffffa',
                    borderRadius: 1,
                    borderWidth: '1px',
                    borderStyle: { xs: 'none', md: 'dashed' },
                    borderColor: theme.palette.border['brand-subtle'],
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <InstitutionHeaderLogo
                    isLoading={institutionIsLoading && !institutionData}
                    logoUrl={institutionData?.logoUrl}
                  />
                </Stack>
              </Stack>

              {/**@NameAndAddress */}
              <Stack
                sx={{
                  paddingTop: '17px',
                  paddingLeft: { xs: 0, md: 3 },
                  paddingRight: { xs: 0, md: 3 },
                }}
              >
                {institutionIsLoading && !institutionData && (
                  <div className="opacity-40">
                    <div className="h-7.5 w-50 mb-1 bg-white rounded-full animate-pulse" />
                  </div>
                )}
                {institutionData && (
                  <Heading2
                    sx={{
                      color: {
                        xs: theme.palette.text.primary,
                        md: theme.palette.text['on-color'],
                      },
                      marginBottom: { xs: 1, md: 0.5 },
                    }}
                  >
                    {institutionData?.name}
                  </Heading2>
                )}

                {institutionIsLoading && !institutionData && (
                  <div className="opacity-40">
                    <div className="h-5.5 w-60 mb-1 bg-white rounded-full animate-pulse" />
                  </div>
                )}

                {institutionData && (
                  <Stack
                    sx={{
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'start', md: 'center' },
                    }}
                  >
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: { xs: 1, md: 0 },
                      }}
                    >
                      <PlaceOutlined
                        sx={{
                          width: 16,
                          height: 16,
                          color: {
                            xs: theme.palette.text.primary,
                            md: theme.palette.text['on-color'],
                          },
                          marginRight: '8px',
                        }}
                      />
                      <button
                        style={{
                          ...VanillaButtonStyle,
                          backgroundColor: 'transparent',
                        }}
                        onClick={goToMaps}
                      >
                        <SmallTextBold
                          sx={{
                            textDecoration: 'underline',
                            color: {
                              xs: theme.palette.text.brand,
                              md: theme.palette.text['on-color'],
                            },
                            marginRight: 1,
                            textAlign: 'start',
                          }}
                        >
                          {institutionData?.address}
                        </SmallTextBold>
                      </button>
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: { xs: 2, md: 0 },
                      }}
                    >
                      <InfoOutlined
                        sx={{
                          width: 16,
                          height: 16,
                          color: {
                            xs: theme.palette.text.primary,
                            md: theme.palette.text['on-color'],
                          },
                          marginRight: '8px',
                        }}
                      />

                      <button
                        style={{
                          ...VanillaButtonStyle,
                          backgroundColor: 'transparent',
                        }}
                        onClick={() => setMoreInfo(true)}
                      >
                        <SmallTextBold
                          sx={{
                            textDecoration: 'underline',
                            color: {
                              xs: theme.palette.text.brand,
                              md: theme.palette.text['on-color'],
                            },
                            marginRight: 1,
                          }}
                        >
                          Informations pratiques {/**@TranslationNeeded */}
                        </SmallTextBold>
                      </button>
                    </Stack>
                    {isSmallScreen && (
                      <>
                        {!institutionData?.pages ||
                        institutionData?.pages?.length === 0 ? (
                          <Stack
                            sx={{
                              borderBottom: 'solid 1px',
                              borderColor: theme.palette.border.subtle,
                              width: '100%',
                              '& > div.MuiTabs-root': {
                                height: '100%',
                                width: '100%',
                                '& button .status-count': {
                                  backgroundColor:
                                    CareerThemeColors[themeKey].color,
                                },
                                '& .MuiTabs-indicator': {
                                  backgroundColor:
                                    CareerThemeColors[themeKey].color,
                                },
                                '& .MuiTab-root.Mui-selected .MuiTypography-root':
                                  { color: 'black' },
                                '& .MuiTab-root:hover': {
                                  backgroundColor: 'transparent',
                                },
                              },
                            }}
                          >
                            <Tabs
                              items={[
                                {
                                  label: tCareer('preview.tabs.presentation'),
                                  'data-testid':
                                    'CareerWebsite__tab_presentation',
                                },
                                {
                                  label: (
                                    <Stack
                                      sx={{
                                        gap: 1,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                      }}
                                    >
                                      {tCareer('preview.tabs.job_offers')}
                                      <Badge
                                        content={totalOffers ?? 0}
                                        status={BadgeStatus.COUNT}
                                      />
                                    </Stack>
                                  ),
                                  'data-testid': 'CareerWebsite__tab_jobOffers',
                                },
                              ]}
                              onChange={handleChangeTab}
                              value={currentTab}
                              variant={isSmallScreen ? 'fullWidth' : 'standard'}
                            />
                          </Stack>
                        ) : (
                          <AppButton
                            variant="secondary"
                            fullWidth
                            sx={{ gap: 1 }}
                            onClick={() => setTabSelector(true)}
                          >
                            <TextBodyBold>
                              {currentTab === institutionData?.pages?.length
                                ? tCareer('preview.tabs.job_offers')
                                : institutionData?.pages?.[currentTab]?.title}
                            </TextBodyBold>
                            <ExpandMoreOutlined
                              sx={{ width: 24, height: 24 }}
                            />
                          </AppButton>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>

            {/**@CareerTabs */}
            <Stack
              sx={{
                borderBottom: { md: 'solid 1px', xs: undefined },
                borderColor: { md: theme.palette.border.subtle, xs: undefined },
                marginBottom: { md: 2, xs: undefined },
              }}
            >
              {/**@TODO - Implement loaders depending on screen size */}
              {institutionIsLoading && !institutionData && (
                <Stack sx={{ flexDirection: 'row', gap: 2, height: 48 }}>
                  <div className="opacity-20">
                    <div className="h-5.5 w-20 mb-1 bg-black rounded-full animate-pulse" />
                  </div>
                  <div className="opacity-20">
                    <div className="h-5.5 w-20 mb-1 bg-black rounded-full animate-pulse" />
                  </div>
                  <div className="opacity-20">
                    <div className="h-5.5 w-20 mb-1 bg-black rounded-full animate-pulse" />
                  </div>
                </Stack>
              )}

              {institutionData && !isSmallScreen && (
                <Stack
                  sx={{
                    '& > div.MuiTabs-root': {
                      height: '100%',
                      width: '100%',
                      '& button .status-count': {
                        backgroundColor: CareerThemeColors[themeKey].color,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: CareerThemeColors[themeKey].color,
                      },
                      '& .MuiTab-root.Mui-selected .MuiTypography-root': {
                        color: 'black',
                      },
                      '& .MuiTab-root:hover': {
                        backgroundColor: 'transparent',
                      },
                    },
                  }}
                >
                  <Tabs
                    items={
                      !institutionData?.pages ||
                      institutionData?.pages?.length === 0
                        ? [
                            {
                              label: tCareer('preview.tabs.presentation'),
                              'data-testid': 'CareerWebsite__tab_presentation',
                            },
                            {
                              label: (
                                <Stack
                                  sx={{
                                    gap: 1,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}
                                >
                                  {tCareer('preview.tabs.job_offers')}
                                  <Badge
                                    content={totalOffers ?? 0}
                                    status={BadgeStatus.COUNT}
                                  />
                                </Stack>
                              ),
                              'data-testid': 'CareerWebsite__tab_jobOffers',
                            },
                          ]
                        : [
                            ...(institutionData?.pages?.map((i) => ({
                              label: i.title,
                            })) ?? []),
                            {
                              label: (
                                <Stack
                                  sx={{
                                    gap: 1,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}
                                >
                                  {tCareer('preview.tabs.job_offers')}
                                  <Badge
                                    content={totalOffers ?? 0}
                                    status={BadgeStatus.COUNT}
                                  />
                                </Stack>
                              ),
                              'data-testid': 'CareerWebsite__tab_jobOffers',
                            },
                          ]
                    }
                    onChange={handleChangeTab}
                    value={currentTab}
                    variant={isSmallScreen ? 'fullWidth' : 'standard'}
                  />
                </Stack>
              )}
            </Stack>

            <Stack
              sx={{
                flexDirection: { md: 'row', xs: 'column' },
                width: '100%',
              }}
            >
              {/**@LeftPanel - Widgets */}
              <Stack
                sx={{
                  flex: 1,
                  gap: { md: 5, xs: 2 },
                  marginBottom: { md: 10, xs: 0 },
                  marginTop: 1,
                }}
              >
                {/**@Widgets */}
                {institutionIsLoading && !institutionData && (
                  <>
                    <Stack>
                      <div className="opacity-20">
                        <div className="h-7.5 w-96 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-7.5 w-60 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-7.5 w-50 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                    </Stack>
                    <Stack>
                      <div className="opacity-20">
                        <div className="h-7.5 w-50 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-7.5 w-60 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-7.5 w-96 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                    </Stack>
                  </>
                )}

                {institutionData && (
                  <>
                    {!institutionData?.pages?.length ? (
                      <>{currentTab === 0 && <PresentationTab slug={slug} />}</>
                    ) : (
                      <>
                        {institutionData?.pages?.map((i, j) => (
                          <Fragment key={`${i.title}-${j}`}>
                            {currentTab === j && (
                              <>
                                {i.widgets.map((k) => (
                                  <Fragment key={k.id}>
                                    <Stack>
                                      {k.type !== 'action' ? (
                                        <CareerWidget widget={k as Widget} />
                                      ) : (
                                        <CareerWidget
                                          widget={k as Widget}
                                          buttonColor={
                                            CareerThemeColors[themeKey].color
                                          }
                                          textColor={
                                            CareerThemeColors[themeKey]
                                              .textButtonColor
                                          }
                                        />
                                      )}
                                    </Stack>
                                    {isSmallScreen && (
                                      <Box
                                        sx={{
                                          backgroundColor:
                                            theme.palette.border.subtle,
                                          height: '1px',
                                          width: '100%',
                                        }}
                                      />
                                    )}
                                  </Fragment>
                                ))}
                              </>
                            )}
                          </Fragment>
                        ))}
                      </>
                    )}

                    {/**@JobListings */}
                    {((!institutionData?.pages?.length && currentTab === 1) ||
                      (institutionData?.pages?.length > 0 &&
                        currentTab === institutionData?.pages?.length)) && (
                      <Suspense fallback={<JobCardSkeleton />}>
                        <JobAdsFiltersStorageProvider storageKey="careerAdsFilters">
                          <JobAdsTab slug={slug} />
                        </JobAdsFiltersStorageProvider>
                      </Suspense>
                    )}
                  </>
                )}
              </Stack>

              {/**@RightPanel - Advantages, etc. */}
              <Stack
                sx={{
                  width: { md: 368, xs: '100%' },
                  marginLeft: { md: 8, xs: 0 },
                  gap: { md: 5, xs: 2 },
                }}
              >
                {institutionIsLoading && !institutionData && !isSmallScreen && (
                  <Stack
                    sx={{
                      gap: 4,
                    }}
                  >
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      <div className="opacity-20">
                        <div className="h-6 w-full mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-6 w-1/2 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-6 w-20 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                    </Stack>
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      <div className="opacity-20">
                        <div className="h-6 w-full mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-6 w-1/2 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                      <div className="opacity-20">
                        <div className="h-6 w-20 mb-1 bg-black rounded-full animate-pulse" />
                      </div>
                    </Stack>
                  </Stack>
                )}

                {institutionData && (!isSmallScreen || currentTab === 0) && (
                  <>
                    {/**@BenefitsBox */}
                    {institutionData?.benefits?.length > 0 && (
                      <Stack sx={{ gap: 2 }}>
                        <Heading2>{tCareer('benefits.title')}</Heading2>
                        <Stack
                          sx={{
                            gap: 2,
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                            paddingBottom: 2,
                          }}
                        >
                          {benefits?.map((item) => (
                            <CareerChip
                              icon={item.customIcon}
                              key={item.itemId}
                              label={tCareer(item.label)}
                            />
                          ))}
                        </Stack>
                        {isSmallScreen && (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.border.subtle,
                              height: '1px',
                              width: '100%',
                            }}
                          />
                        )}
                      </Stack>
                    )}

                    {/**@AddressBox */}
                    {institutionData?.address && (
                      <Stack sx={{ gap: 2 }}>
                        <Heading2>{tCareer('contact.find_us')}</Heading2>
                        <button
                          style={{
                            ...VanillaButtonStyle,
                            backgroundColor: theme.palette.background.subtle,
                          }}
                          onClick={goToMaps}
                        >
                          <Stack
                            sx={{
                              flexDirection: 'row',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <PlaceOutlined
                              sx={{
                                color: theme.palette.text.primary,
                                width: 16,
                                height: 16,
                              }}
                            />
                            <SmallTextBold
                              sx={{
                                color: institutionData?.theme
                                  ? CareerThemeColors[themeKey].color
                                  : theme.palette.text.primary,
                                textDecoration: 'underline',
                                textAlign: 'start',
                              }}
                            >
                              {institutionData?.address}
                            </SmallTextBold>
                          </Stack>
                        </button>
                        <button
                          style={{ ...VanillaButtonStyle, width: '100%' }}
                          onClick={goToMaps}
                        >
                          <img
                            alt="google map"
                            src={mapsStaticUrl}
                            style={{ width: '100%' }}
                          />
                        </button>
                        {isSmallScreen && (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.border.subtle,
                              height: '1px',
                              width: '100%',
                            }}
                          />
                        )}
                      </Stack>
                    )}

                    {/**@FindUs */}
                    {institutionData?.accessDetails && (
                      <Stack sx={{ gap: 2 }}>
                        <Heading2>
                          {tCareer('accessModes.details.title')}
                        </Heading2>
                        <Stack
                          sx={{
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: 2,
                          }}
                        >
                          {accessModes.map((item) => (
                            <CareerChip
                              icon={item.customIcon}
                              key={item.itemId}
                              label={tCareer(item.label)}
                            />
                          ))}
                        </Stack>
                        <TextBody>{institutionData?.accessDetails}</TextBody>
                        {isSmallScreen && (
                          <Box
                            sx={{
                              backgroundColor: theme.palette.border.subtle,
                              height: '1px',
                              width: '100%',
                            }}
                          />
                        )}
                      </Stack>
                    )}

                    {/**@ContactBox */}
                    {(institutionData?.contactPersonn ||
                      institutionData?.contactEmail) && (
                      <Stack>
                        <Heading2 sx={{ marginBottom: 2 }}>
                          {tCareer('contact.contact_us')}
                        </Heading2>
                        <div>
                          {institutionData?.contactPersonn && (
                            <TextBodyBold
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              {institutionData?.contactPersonn}
                            </TextBodyBold>
                          )}
                          {institutionData?.contactEmail && (
                            <a
                              href={`mailto:${institutionData?.contactEmail}`}
                              rel="noopener noreferrer"
                              target="_blank"
                              style={{
                                color: theme.palette.text.primary,
                                textDecoration: 'none',
                              }}
                            >
                              <Stack
                                sx={{
                                  flexDirection: 'row',
                                  gap: 1,
                                  alignItems: 'center',
                                }}
                              >
                                <MailOutlined
                                  sx={{ width: 16, height: 16 }}
                                  htmlColor={theme.palette.text.primary}
                                />
                                <SmallTextBold
                                  sx={{
                                    textDecoration: 'underline',
                                    color: CareerThemeColors[themeKey].color,
                                  }}
                                >
                                  {institutionData?.contactEmail}
                                </SmallTextBold>
                              </Stack>
                            </a>
                          )}
                        </div>
                      </Stack>
                    )}

                    {/**@SocialsBox */}
                    {socials.length > 0 && (
                      <Stack
                        sx={{
                          backgroundColor: theme.palette.background.surface,
                          border: '1px solid',
                          borderColor: theme.palette.border['brand-subtle'],
                          padding: 2,
                          borderRadius: 2,
                          boxShadow:
                            '0 2px 4px 0 rgba(25, 23, 21, 0.03), 0 8px 8px 0 rgba(25, 23, 21, 0.02)',
                        }}
                      >
                        <Stack
                          sx={{
                            gap: 1,
                          }}
                        >
                          <Heading4>{tCareer('contact.follow_us')}</Heading4>
                          <TextBody
                            sx={{
                              marginBottom: 2,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {tCareer('contact.follow_institution')}
                          </TextBody>
                        </Stack>
                        <Stack
                          sx={{
                            flexDirection: 'row',
                            gap: 2,
                            alignItems: 'center',
                          }}
                        >
                          {socials.map(({ social, url }) => (
                            <IconLink
                              href={url ?? undefined}
                              data-testid={`CareerWebsite__social_network__icon_${social.brandName}`}
                              target="_blank"
                              rel="noopener"
                              key={social.brandName}
                            >
                              {social.renderIcon()}
                            </IconLink>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {isSmallScreen && <Box sx={{ pb: 6 }} />}
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
      {institutionData && (
        <>
          <MoreCareerInfoDialog
            isOpen={moreInfo}
            handleClose={() => setMoreInfo(false)}
            establishmentUri={institutionData?.establishmentLink as string}
            regionUri={institutionData?.regionLink as string}
            cityUri={institutionData?.cityLink as string}
          />
          {isSmallScreen && (
            <PageTabSelectDialog
              pageList={[
                ...(institutionData?.pages?.map((i) => i.title) ?? []),
                tCareer('preview.tabs.job_offers'),
              ]}
              handleChangePage={handleChangePage}
              isOpen={tabSelector}
              handleClose={() => setTabSelector(false)}
            />
          )}
        </>
      )}
    </>
  )
}
